import { ARCHETYPE_ORDER } from '../data/types';
import { ARCHETYPES } from '../data/archetypes';
import type { Scores } from '../data/types';
import styles from './Constellation.module.css';

interface ConstellationProps {
  scores: Scores;
  /** Whether to play the draw-in animation. */
  animated?: boolean;
}

const VB = 600;
const CENTER = VB / 2;
const RADIUS = 220;

/** Hexagonal vertex coordinates around the center, indexed by archetype order. */
function vertex(index: number, distance: number): [number, number] {
  // Place servant at top, then go clockwise
  const angle = (-Math.PI / 2) + (index * (Math.PI * 2)) / 6;
  return [CENTER + Math.cos(angle) * distance, CENTER + Math.sin(angle) * distance];
}

function labelOffset(index: number, distance: number): [number, number] {
  const angle = (-Math.PI / 2) + (index * (Math.PI * 2)) / 6;
  return [CENTER + Math.cos(angle) * distance, CENTER + Math.sin(angle) * distance];
}

export function Constellation({ scores, animated = true }: ConstellationProps) {
  // Polygon: each archetype's vertex distance is proportional to its score
  const polygonPoints = ARCHETYPE_ORDER.map((arc, i) => {
    const distance = (scores[arc] / 100) * RADIUS;
    return vertex(i, distance).join(',');
  }).join(' ');

  // Reference rings (33%, 66%, 100%)
  const rings = [0.33, 0.66, 1].map((pct) => {
    const points = Array.from({ length: 6 }, (_, i) =>
      vertex(i, RADIUS * pct).join(',')
    ).join(' ');
    return { pct, points };
  });

  // Text accessibility summary
  const ariaSummary = ARCHETYPE_ORDER.map(
    (a) => `${ARCHETYPES[a].label}: ${scores[a]} of 100`
  ).join('; ');

  return (
    <figure className={styles.figure} role="img" aria-label={ariaSummary}>
      <svg
        viewBox={`0 0 ${VB} ${VB}`}
        className={styles.svg}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="hnn-nebula" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#1A2D4F" stopOpacity="0.6" />
            <stop offset="60%" stopColor="#2D0E29" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0F0A1F" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="hnn-star-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFF8E0" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#C9A96E" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#C9A96E" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* nebula backdrop */}
        <rect x="0" y="0" width={VB} height={VB} fill="url(#hnn-nebula)" />

        {/* distant stars (decorative) */}
        {Array.from({ length: 28 }, (_, i) => {
          // Deterministic pseudo-random placement
          const seed = i * 37 + 7;
          const x = ((seed * 9301 + 49297) % 233280) / 233280 * VB;
          const y = ((seed * 49297 + 9301) % 233280) / 233280 * VB;
          const r = 0.4 + (((seed * 7919) % 100) / 100) * 0.8;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={r}
              fill="#F8F0DD"
              opacity={0.2 + (((seed * 6311) % 100) / 100) * 0.4}
            />
          );
        })}

        {/* reference rings (hex outlines) */}
        {rings.map((ring, i) => (
          <polygon
            key={i}
            points={ring.points}
            fill="none"
            stroke="#C9A96E"
            strokeOpacity={0.12 + i * 0.05}
            strokeWidth={1}
          />
        ))}

        {/* axis lines from center to each vertex */}
        {ARCHETYPE_ORDER.map((arc, i) => {
          const [x, y] = vertex(i, RADIUS);
          return (
            <line
              key={`axis-${arc}`}
              x1={CENTER}
              y1={CENTER}
              x2={x}
              y2={y}
              stroke="#C9A96E"
              strokeOpacity={0.1}
              strokeWidth={1}
            />
          );
        })}

        {/* the constellation polygon */}
        <polygon
          className={animated ? styles.polygonDraw : ''}
          points={polygonPoints}
          fill="#C9A96E"
          fillOpacity={0.15}
          stroke="#C9A96E"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />

        {/* stars at each vertex, sized by score */}
        {ARCHETYPE_ORDER.map((arc, i) => {
          const distance = (scores[arc] / 100) * RADIUS;
          const [x, y] = vertex(i, distance);
          const def = ARCHETYPES[arc];
          const starSize = 4 + (scores[arc] / 100) * 6;
          const glowSize = 18 + (scores[arc] / 100) * 12;

          return (
            <g
              key={arc}
              className={animated ? styles.starGroup : ''}
              style={{ animationDelay: `${1.8 + i * 0.15}s` }}
            >
              <circle cx={x} cy={y} r={glowSize} fill="url(#hnn-star-glow)" />
              <circle cx={x} cy={y} r={starSize} fill={def.accent} />
              <circle cx={x} cy={y} r={starSize * 0.4} fill="#FFFFFF" opacity="0.85" />
            </g>
          );
        })}

        {/* center ornament */}
        <g>
          <circle cx={CENTER} cy={CENTER} r={6} fill="#C9A96E" opacity="0.4" />
          <circle cx={CENTER} cy={CENTER} r={2.5} fill="#F8F0DD" />
        </g>

        {/* archetype labels around the perimeter */}
        {ARCHETYPE_ORDER.map((arc, i) => {
          const [x, y] = labelOffset(i, RADIUS + 38);
          const def = ARCHETYPES[arc];
          // Tweak text-anchor based on position so labels don't overflow center
          const angleIdx = i % 6;
          const anchor: 'start' | 'middle' | 'end' =
            angleIdx === 0 || angleIdx === 3 ? 'middle' : angleIdx < 3 ? 'start' : 'end';
          const labelY =
            angleIdx === 0 ? y : angleIdx === 3 ? y + 4 : y + 5;

          return (
            <g key={`label-${arc}`}>
              <text
                x={x}
                y={labelY}
                textAnchor={anchor}
                fill={def.accent}
                className={styles.label}
              >
                {def.id}
              </text>
              <text
                x={x}
                y={labelY + 16}
                textAnchor={anchor}
                fill="#C8B89A"
                className={styles.score}
              >
                {scores[arc]}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
